import React, { useState, useEffect, useMemo } from "react";
import {
  Typography, Grid, Button, Box, TextField, FormControl,
  MenuItem, Select, Divider, Chip, InputAdornment, FormHelperText,
  Avatar, Paper, CircularProgress, Snackbar, Alert, Autocomplete, 
  Switch, FormControlLabel
} from "@mui/material";
import { useSearchParams } from 'react-router-dom';
import PhotoCameraIcon from "@mui/icons-material/PhotoCamera";
import InstagramIcon from "@mui/icons-material/Instagram";
import YouTubeIcon from '@mui/icons-material/YouTube';
import FacebookIcon from '@mui/icons-material/Facebook';
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import XIcon from '@mui/icons-material/X';
import CloseIcon from '@mui/icons-material/Close';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import PeopleIcon from '@mui/icons-material/People';
import LinkIcon from '@mui/icons-material/Link';
import AddIcon from '@mui/icons-material/Add';
import PropTypes from "prop-types";

import { getSubcategories } from "../../../actions/categories";
import { addProfilePage, updateProfilePage, inviteFacilitator } from "../../../actions/profile";
import { searchUsers } from "../../../actions/users";
import { isEmpty, areAllValuesEmpty, urlImg } from "../../common/helpers";
import random from "../../common/random";
import typeImg from "../../common/typeImg";

import { useSelector, useDispatch } from "react-redux";

const FormPagina = ({
  irA,
}) => {
  const [searchParams] = useSearchParams();
  const editId = searchParams.get("edit");
  const draftId = searchParams.get("draft");
  const typeParam = searchParams.get("type");
  const pageId = editId || draftId;

  const dispatch = useDispatch();
  const categories = useSelector(state => state.cat.categories);
  const direcciones = useSelector(state => state.profile.profile.direcciones);
  const paginas = useSelector(state => state.profile.profile.paginas);
  const currentUser = useSelector(state => state.user.user);
  const searchResults = useSelector(state => state.user.users);
  const userServices = useSelector(state => state.profile.profile.servicios);
  const profileId = useSelector(state => state.profile.profile.user);

  const [service, setService] = useState({
    nombre: "",
    descripcion: "",
    incluye: "",
    adicionalInfo: "",
    categoria: "",
    subcategoria: "",
    ubicacion: "",
    // Social Media
    instagram: "",
    youtube: "",
    facebook: "",
    tiktok: "",
    linkedin: "",
    x: "",
    tags: [],
    status: true,
  });

  const [tagInput, setTagInput] = useState("");

  const [memberInput, setMemberInput] = useState("");
  const [selectedMember, setSelectedMember] = useState(null);
  const [members, setMembers] = useState([]);
  const [selectedServices, setSelectedServices] = useState([]);
  const [subcategorias, setSubCat] = useState([]);
  const [banner, setBanner] = useState(null);
  const [bannerPreview, setBannerPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [errores, setErrores] = useState({});
  const [alert, setAlert] = useState({ open: false, message: "", severity: "info" });

  const isGroup = useMemo(() => {
    if (typeParam === 'group') return true;
    if (typeParam === 'independent') return false;
    return members.length > 1;
  }, [typeParam, members.length]);

  useEffect(() => {
    if (currentUser && currentUser._id && members.length === 0) {
      setMembers([{
        usuario: {
          _id: currentUser._id,
          name: currentUser.name,
          apellido: currentUser.apellido,
          avatar: currentUser.avatar,
          versionAvatar: currentUser.versionAvatar,
        },
        status: 1, // Aceptado
        isAdmin: true
      }]);
    }
  }, [currentUser]);

  useEffect(() => {
    if (pageId && paginas) {
      const pageToEdit = paginas.find(p => p._id.toString() === pageId.toString());
      if (pageToEdit) {
        setMembers(pageToEdit.miembros || []);
        setSelectedServices((pageToEdit.servicios || []).map(s => {
          const serviceId = s.servicio?._id || s.servicio || s._id || s;
          const facilitators = s.facilitadores || (s.facilitador ? [s.facilitador] : []);
          return { servicio: serviceId, facilitadores: facilitators };
        }));
        setService({
          nombre: pageToEdit.nombre || "",
          descripcion: pageToEdit.descripcion || "",
          incluye: pageToEdit.incluye || "",
          adicionalInfo: pageToEdit.infoAdicional || "", // careful with field name mapping
          categoria: pageToEdit.categoria || "",
          subcategoria: pageToEdit.subcategoria || "",
          ubicacion: pageToEdit.ubicacion || "",
          instagram: pageToEdit.redes?.instagram || "",
          youtube: pageToEdit.redes?.youtube || "",
          facebook: pageToEdit.redes?.facebook || "",
          tiktok: pageToEdit.redes?.tiktok || "",
          linkedin: pageToEdit.redes?.linkedin || "",
          x: pageToEdit.redes?.x || "",
          tags: pageToEdit.tags || [],
          status: pageToEdit.status !== undefined ? pageToEdit.status : true,
        });

        if (pageToEdit.img) {
          setBannerPreview(`${urlImg}/paginas/${pageId}/${pageToEdit.img}`);
          // Note: actual file object 'banner' remains null unless user uploads new one
        }
      }
    }
  }, [pageId, paginas]);

  useEffect(() => {
    if (service.categoria) {
      loadSubCat(service.categoria);
    }
  }, [service.categoria]);

  const loadSubCat = async cat => {
    const res = await getSubcategories(cat);
    setSubCat(res);
  }

  const handleMemberDelete = id => {
    setMembers(prev => prev.filter(m => (m.usuario ? m.usuario._id !== id : m._id !== id) || m.isAdmin));
  }

  const handleSearchChange = (event, newValue) => {
    setMemberInput(newValue);
    if (newValue.length >= 3) {
      dispatch(searchUsers(newValue));
    }
  }

  const handleAddMember = (event, value) => {
    setSelectedMember(value);
  }

  const handleCommitMember = () => {
    if (selectedMember && !members.find(m => m.usuario ? m.usuario._id === selectedMember._id : m._id === selectedMember._id)) {
      setMembers(prev => [...prev, { usuario: selectedMember, status: 0 }]);
      setSelectedMember(null);
      setMemberInput("");
    }
  }

  const getStatusText = (status) => {
    switch (status) {
      case 0: return "PENDIENTE";
      case 1: return "ACEPTADO";
      case 2: return "RECHAZADO";
      default: return "";
    }
  }

  const getStatusColor = (status) => {
    switch (status) {
      case 0: return "#FFD074"; // Warning/Pending
      case 1: return "#59C39A"; // Success/Accepted
      case 2: return "#FF827A"; // Error/Rejected
      default: return "gray";
    }
  }

  const handleSaveInvitation = async (serviceId, facilitatorId) => {
    if (!pageId) {
      setAlert({
        open: true,
        message: "Primero debes guardar la página (como borrador o publicada) para poder enviar invitaciones individuales.",
        severity: "warning"
      });
      return;
    }

    if (!facilitatorId) {
      setAlert({
        open: true,
        message: "Por favor selecciona un facilitador antes de guardar.",
        severity: "error"
      });
      return;
    }

    const res = await dispatch(inviteFacilitator(pageId, facilitatorId, serviceId));
    
    if (res.success) {
      setAlert({
        open: true,
        message: "¡Invitación enviada exitosamente!",
        severity: "success"
      });
    } else {
      setAlert({
        open: true,
        message: res.msg || "Error al enviar la invitación",
        severity: "error"
      });
    }
  }

  const ServiceCard = ({ service }) => {
    const isAdded = selectedServices.some(s => s.servicio === service._id);
    const mainImg = service.img && service.img.length > 0 ? service.img[0].nombre : null;
    const imgUrl = mainImg
      ? `${urlImg}/servicios/${service._id}/${mainImg}`
      : "https://via.placeholder.com/150";

    return (
      <Paper
        variant="outlined"
        sx={{
          p: 2,
          mb: 2,
          borderRadius: "12px",
          display: "flex",
          flexDirection: { xs: "column", sm: "row" },
          gap: 2,
          transition: "box-shadow 0.3s ease",
          "&:hover": {
            boxShadow: "0 4px 20px rgba(0,0,0,0.08)"
          }
        }}
      >
        <Box
          sx={{
            width: { xs: "100%", sm: 200 },
            height: 150,
            borderRadius: "8px",
            overflow: "hidden",
            flexShrink: 0
          }}
        >
          <img
            src={imgUrl}
            alt={service.nombre}
            style={{ width: "100%", height: "100%", objectFit: "cover" }}
          />
        </Box>
        <Box sx={{ flex: 1, display: "flex", flexDirection: "column" }}>
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", mb: 1 }}>
            <Box>
              <Typography variant="subtitle1" color="primary" fontWeight="bold">
                {service.nombre}
              </Typography>
              <Typography variant="body2" >
                {service.descripcion}
              </Typography>
            </Box>
            <Box sx={{ textAlign: "right" }}>
              <Typography variant="h6" color="primary">
                ${service.costoServicio || "0.00"}
              </Typography>
              <Typography variant="caption">
                por {service.tipoCosto || "servicio"}
              </Typography>
            </Box>
          </Box>

          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              <AccessTimeIcon sx={{ fontSize: 18, color: "primary.main" }} />
              <Typography variant="caption" >
                Duración: {service.duracionEstimada} {service.unidadDuracion}
              </Typography>
            </Grid>
            <Grid item sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              <LocationOnIcon sx={{ fontSize: 18, color: "primary.main" }} />
              <Typography variant="caption" >
                {service.modalidades?.includes('Remoto') ? 'Remoto' : 'Presencial'}
              </Typography>
            </Grid>
            <Grid item sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              <PeopleIcon sx={{ fontSize: 18, color: "primary.main" }} />
              <Typography variant="caption">
                Grupo de {service.capacidad || "1"} personas
              </Typography>
            </Grid>
          </Grid>

          <Box sx={{ mt: "auto", display: "flex", justifyContent: "flex-end", gap: 1 }}>
            <Button
              variant="contained"
              size="small"
              startIcon={<LinkIcon />}
              sx={{
                bgcolor: "#64D2B1",
                color: "white",
                textTransform: "none",
                borderRadius: "8px",
                "&:hover": { bgcolor: "#52ae92" }
              }}
              onClick={() => irA(`/user/servicio/${service._id}`)}
            >
              Ver
            </Button>
            <Button
              variant="contained"
              size="small"
              startIcon={isAdded ? <CloseIcon /> : <AddIcon />}
              sx={{
                bgcolor: isAdded ? "#e57373" : "#0E2465",
                color: "white",
                textTransform: "none",
                borderRadius: "8px",
                "&:hover": { bgcolor: isAdded ? "#d32f2f" : "#0a1b4d" }
              }}
              onClick={() => handleToggleService(service._id)}
            >
              {isAdded ? "Quitar" : "Añadir +"}
            </Button>
          </Box>
          {isGroup && (
            <Box sx={{ mt: 2, pt: 2, borderTop: "1px solid #f0f0f0" }}>
              <Typography variant="body2" color="primary" fontWeight="bold" sx={{ mb: 1 }}>
                Facilitadores asignados
              </Typography>
              
              <Autocomplete
                multiple
                size="small"
                options={members.map(m => m.usuario || m)}
                getOptionLabel={(option) => `${option.name} ${option.apellido || ""}`}
                value={(() => {
                  const rel = selectedServices.find(s => s.servicio === service._id);
                  if (!rel || !rel.facilitadores) return [];
                  return rel.facilitadores.map(fId => {
                    const member = members.find(m => (m.usuario?._id || m._id) === fId.toString());
                    return member?.usuario || member;
                  }).filter(f => f);
                })()}
                onChange={(e, newValue) => handleAssignFacilitators(service._id, newValue.map(v => v._id))}
                renderInput={(params) => (
                  <TextField 
                    {...params} 
                    placeholder="Busca y selecciona facilitadores" 
                    variant="outlined" 
                    InputProps={{
                      ...params.InputProps,
                      startAdornment: (
                        <>
                          <InputAdornment position="start">
                            <Typography variant="body2" color="#737791" sx={{ fontWeight: 'bold' }}>@</Typography>
                          </InputAdornment>
                          {params.InputProps.startAdornment}
                        </>
                      ),
                    }}
                  />
                )}
                renderOption={(props, option) => (
                  <li {...props}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Avatar
                        src={`${urlImg}/w_40,h_40,c_fill/v${option.versionAvatar}/users/${option._id}/${option.avatar}`}
                        sx={{ width: 24, height: 24 }}
                      />
                      <Typography variant="body2">{option.name} {option.apellido}</Typography>
                    </Box>
                  </li>
                )}
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => (
                    <Chip
                      avatar={
                        <Avatar
                          src={`${urlImg}/w_40,h_40,c_fill/v${option.versionAvatar}/users/${option._id}/${option.avatar}`}
                        />
                      }
                      label={`${option.name} ${option.apellido || ""}`}
                      {...getTagProps({ index })}
                      sx={{ borderRadius: '8px', bgcolor: '#f0f2f5' }}
                      onDelete={() => {
                        const newIds = value.filter((_, i) => i !== index).map(v => v._id);
                        handleAssignFacilitators(service._id, newIds);
                      }}
                    />
                  ))
                }
              />

              <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
                <Button 
                  variant="contained" 
                  size="small" 
                  color="primary"
                  onClick={async () => {
                    const rel = selectedServices.find(s => s.servicio === service._id);
                    if (rel && rel.facilitadores && rel.facilitadores.length > 0) {
                      // Send invitations for all facilitators (backend handles existing ones)
                      for (const fId of rel.facilitadores) {
                        await handleSaveInvitation(service._id, fId);
                      }
                    }
                  }}
                  sx={{ textTransform: 'none', fontWeight: 'bold', fontSize: '14px', px: 4, py: 1, borderRadius: '6px', bgcolor: '#0E2465' }}
                >
                  Guardar invitaciones
                </Button>
              </Box>
            </Box>
          )}
        </Box>
      </Paper>
    );
  }

  const handleAssignFacilitators = (serviceId, facilitatorIds) => {
    setSelectedServices(prev => {
      const exists = prev.find(s => s.servicio === serviceId);
      if (exists) {
        return prev.map(s => s.servicio === serviceId ? { ...s, facilitadores: facilitatorIds } : s);
      } else {
        return [...prev, { servicio: serviceId, facilitadores: facilitatorIds }];
      }
    });
  }

  const handleToggleService = (id) => {
    setSelectedServices(prev => {
      if (prev.find(s => s.servicio === id)) {
        return prev.filter(s => s.servicio !== id);
      } else {
        return [...prev, { servicio: id, facilitadores: [] }];
      }
    });
  }

  const handleAddNewAddress = async () => {
    const hasContent = Object.values(service).some(val => {
      if (typeof val === 'string') return val.trim() !== "";
      if (Array.isArray(val)) return val.length > 0;
      return false;
    }) || banner !== null || bannerPreview !== null;

    if (hasContent) {
      await handleSubmit(null, true, false);
    }
    irA('/mi-perfil');
  };

  const handleCreateNewService = async (e) => {
    // Save as draft first
    const success = await handleSubmit(e, true, false); // Add 'redirect' param to handleSubmit
    if (success) {
      irA('/user/crear-servicio/0');
    }
  }

  const handleAddTag = () => {
    if (tagInput.trim() !== "" && !service.tags.includes(tagInput.trim())) {
      setService(prev => ({
        ...prev,
        tags: [...prev.tags, tagInput.trim()]
      }));
      setTagInput("");
    }
  }

  const handleRemoveTag = (tagToDelete) => {
    setService(prev => ({
      ...prev,
      tags: prev.tags.filter(tag => tag !== tagToDelete)
    }));
  }

  const handleChange = (field, value) => {
    setService(prev => ({
      ...prev,
      [field]: value
    }));
    if (errores[field]) {
      setErrores(prev => ({ ...prev, [field]: null }));
    }
  }

  const handleBannerChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setBanner(file);
      setBannerPreview(URL.createObjectURL(file));
      if (errores.banner) {
        setErrores(prev => ({ ...prev, banner: null }));
      }
    }
  }

  const handleSubmit = async (e, borrador = false, redirect = true) => {
    if (e) e.preventDefault();
    setLoading(true);

    if (borrador) {
      // Check if at least one field has data
      const hasContent = Object.values(service).some(val => {
        if (typeof val === 'string') return val.trim() !== "";
        if (Array.isArray(val)) return val.length > 0;
        if (typeof val === 'object' && val !== null) return !areAllValuesEmpty(val);
        return false;
      }) || banner !== null || bannerPreview !== null;
      if (!hasContent) {
        setAlert({
          open: true,
          message: "Se requiere al menos un campo para guardar el borrador",
          severity: "warning"
        });
        setLoading(false);
        return;
      }
    } else {
      // Validation for publish
      const requiredFields = ['nombre', 'descripcion', 'categoria'];
      const newErrors = {};

      requiredFields.forEach(field => {
        if (!service[field] || service[field].trim() === '') {
          newErrors[field] = "Este campo es requerido";
        }
      });

      if (!service.ubicacion || service.ubicacion.trim() === '') {
        newErrors.ubicacion = "Este campo es requerido";
      }

      const hasImage = banner !== null || bannerPreview !== null;
      if (!hasImage) {
        newErrors.banner = "La imagen es requerida";
      }

      if (Object.keys(newErrors).length > 0) {
        setErrores(newErrors);
        setLoading(false);
        return false;
      }
    }

    const formData = {
      ...service,
      borrador,
      status: service.status,
      tags: service.tags,
      infoAdicional: service.adicionalInfo
    };

    formData.miembros = members.map(m => m.usuario ? m.usuario._id : m._id);
    
    // For independent pages, automatically assign owner as facilitator for all services
    const ownerId = currentUser?._id || members[0]?.usuario?._id || members[0]?._id;
    formData.servicios = isGroup 
      ? selectedServices 
      : selectedServices.map(s => ({ ...s, facilitadores: [ownerId] }));

    if (!pageId) {
      // formData.servicios = []; // Keeping selected ones
    }

    // Prepare image data if banner exists
    let imgData = null;
    if (banner) {
      const identificador = random(7);
      const nombreImg = identificador + typeImg(banner);

      formData.img = nombreImg;

      imgData = new FormData();
      imgData.append('file', banner);
      imgData.append('nombre', identificador);
    }

    let success = false;
    if (pageId) {
      console.log(formData, pageId, "siii");
      success = await dispatch(updateProfilePage(formData, pageId, imgData));
      console.log(success);
    } else {
      success = await dispatch(addProfilePage(formData, imgData));
    }

    if (success) {
      if (redirect) {
        irA('/user/servicios', { state: { tab: 1 } });
      }
      return true;
    } else {
      setLoading(false);
    }
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
        <Typography variant="h6" color="primary" sx={{ fontWeight: 'bold' }}>
          {pageId ? "Editar Página de Servicio" : "Crear Página de Servicio"}
        </Typography>
        <FormControlLabel
          control={
            <Switch
              checked={service.status}
              onChange={(e) => handleChange("status", e.target.checked)}
              color="primary"
              name="status"
            />
          }
          label={
            <Typography variant="body2" color="primary" sx={{ fontWeight: 'bold' }}>
              {service.status ? "Página Activa" : "Página Inactiva"}
            </Typography>
          }
        />
      </Box>
      <Typography
        variant="body2"
        color="#737791"
        sx={{ marginBottom: 3 }}
      >
        Completa la información de tu página de servicio para publicarlo
      </Typography>
      <form onSubmit={handleSubmit}>
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item size={12}>
            <Typography variant="body1" color="primary" fontWeight={600}>
              Información Básica
            </Typography>
            <Divider sx={{ mb: 3 }} />

            <Typography variant="body2" color="primary" fontWeight={600} sx={{ mb: 1 }}>
              Título de la Página<span style={{ color: 'red' }}>*</span>
            </Typography>
            <TextField
              id="service-name"
              placeholder="Ej: Escuela de Repostería Creativa"
              name="nombre"
              fullWidth
              value={service.nombre}
              onChange={(e) => handleChange("nombre", e.target.value)}
              variant="outlined"
              error={!!errores.nombre}
              helperText={errores.nombre}
            />
          </Grid>
          <Grid item size={12}>
            <Grid container justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
              <Grid item>
                <Typography variant="body2" color="primary" fontWeight={600}>
                  Descripción<span style={{ color: 'red' }}>*</span>
                </Typography>
              </Grid>
              <Grid item>
                <Typography variant="caption" color="textSecondary">
                  {service.descripcion.length}/250
                </Typography>
              </Grid>
            </Grid>
            <TextField
              name="descripcion"
              placeholder="Describe tu página de servicio"
              multiline
              rows={4}
              variant="outlined"
              fullWidth
              value={service.descripcion}
              onChange={(e) => handleChange("descripcion", e.target.value.slice(0, 250))}
              error={!!errores.descripcion}
              helperText={errores.descripcion}
            />
          </Grid>

          <Grid item size={12}>
            <Typography
              variant="body1"
              color="primary"
              fontWeight={600}
              sx={{ mt: 3, mb: 1 }}
            >
              Redes Sociales
            </Typography>
            <Divider sx={{ mb: 3 }} />

            <Grid container spacing={3} sx={{ mb: 4 }}>
              {[
                { id: 'instagram', title: 'Instagram', placeholder: '@usuario', icon: <InstagramIcon fontSize="small" /> },
                { id: 'facebook', title: 'Facebook', placeholder: 'facebook.com/usuario', icon: <FacebookIcon fontSize="small" /> },
                { id: 'tiktok', title: 'TikTok', placeholder: '@usuario', icon: <img src="https://cdn-icons-png.flaticon.com/512/3046/3046121.png" alt="tiktok" style={{ width: 18, height: 18 }} /> },
                { id: 'youtube', title: 'YouTube', placeholder: 'youtube.com/@usuario', icon: <YouTubeIcon fontSize="small" /> },
                { id: 'x', title: 'X (Twitter)', placeholder: '@usuario', icon: <XIcon fontSize="small" /> },
                { id: 'linkedin', title: 'LinkedIn', placeholder: 'linkedin.com/in/usuario', icon: <LinkedInIcon fontSize="small" /> }
              ].map((social) => (
                <Grid item size={{ xs: 12, md: 6 }} key={social.id}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <Box sx={{ display: 'flex', color: 'primary.main' }}>
                      {social.icon}
                    </Box>
                    <Typography variant="body2" color="primary" fontWeight={600}>
                      {social.title}
                    </Typography>
                  </Box>
                  <TextField
                    fullWidth
                    placeholder={social.placeholder}
                    value={service[social.id]}
                    onChange={(e) => handleChange(social.id, e.target.value)}
                  />
                </Grid>
              ))}
            </Grid>
          </Grid>

          <Grid item size={12}>
            <Typography
              variant="body1"
              color="primary"
              fontWeight={600}
              sx={{ mt: 3, mb: 1 }}
            >
              Etiquetas / Palabras Clave
            </Typography>
            <Divider sx={{ mb: 3 }} />

            <Grid container spacing={2} sx={{ mb: 4 }} alignItems="center">
              <Grid item size={{ xs: 12, md: 4 }}>
                <TextField
                  fullWidth
                  placeholder="Escribe una etiqueta"
                  value={tagInput}
                  onChange={(e) => setTagInput(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddTag())}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Typography color="primary" sx={{ fontWeight: 'bold' }}>#</Typography>
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item size={{ xs: 12, md: 2 }}>
                <Button
                  variant="contained"
                  color="primary"
                  fullWidth
                  onClick={handleAddTag}
                  sx={{ height: '56px', fontWeight: 600, textTransform: 'none' }}
                >
                  Agregar
                </Button>
              </Grid>
              <Grid item size={12}>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {service.tags && service.tags.map((tag, index) => (
                    <Chip
                      key={index}
                      label={tag}
                      onDelete={() => handleRemoveTag(tag)}
                      color="primary"
                      variant="outlined"
                      sx={{ borderRadius: '8px', fontWeight: 600 }}
                    />
                  ))}
                </Box>
              </Grid>
            </Grid>
          </Grid>

          <Grid item size={12}>
            <Typography variant="body1" color="primary" fontWeight={600} sx={{ mt: 3 }}>
              Banner de la Página
            </Typography>
            <Divider sx={{ mb: 3 }} />
            <Typography variant="body2" color="primary" fontWeight={600} sx={{ mb: 1 }}>
              Imagen de Portada<span style={{ color: 'red' }}>*</span><br />
               (El banner debe de medir 1088 x 350 px para una mejor visualización)
            </Typography>
            {/* Vista previa a escala reducida manteniendo proporción real 1088x350 */}
            <Paper
              variant="outlined"
              component="label"
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                position: 'relative',
                width: '100%',
                mx: 'auto',
                aspectRatio: '1088 / 350',
                borderRadius: 1,
                borderStyle: 'dashed',
                cursor: 'pointer',
                overflow: 'hidden',
                bgcolor: '#fafafa',
                borderColor: errores.banner ? '#d32f2f' : 'rgba(0, 0, 0, 0.23)',
                '&:hover .banner-overlay': { opacity: 1 },
              }}
            >
              <input
                type="file"
                hidden
                accept="image/*"
                onChange={handleBannerChange}
              />

              {bannerPreview ? (
                <>
                  {/* Imagen posicionada absolute para llenar todo el contenedor */}
                  <Box sx={{ position: 'absolute', inset: 0 }}>
                    <img
                      src={bannerPreview}
                      alt="Banner"
                      style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                      crossOrigin="anonymous"
                    />
                  </Box>
                  {/* Overlay visible al hacer hover para indicar que se puede cambiar */}
                  <Box
                    className="banner-overlay"
                    sx={{
                      position: 'absolute',
                      inset: 0,
                      bgcolor: 'rgba(0,0,0,0.45)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: 1,
                      opacity: 0,
                      transition: 'opacity 0.2s',
                    }}
                  >
                    <PhotoCameraIcon sx={{ color: 'white' }} />
                    <Typography sx={{ color: 'white', fontWeight: 500 }}>Cambiar banner</Typography>
                  </Box>
                </>
              ) : (
                // Placeholder centrado por el display flex del Paper
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <PhotoCameraIcon color={errores.banner ? 'error' : 'action'} />
                  <Typography color={errores.banner ? 'error' : 'primary'}>Agregar banner</Typography>
                </Box>
              )}
            </Paper>
            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block', textAlign: 'center' }}>
              El banner medirá 1088 x 350 px
            </Typography>
            {errores.banner && <FormHelperText error>{errores.banner}</FormHelperText>}
          </Grid>

          <Grid item size={12}>
            <Typography variant="body1" color="primary" fontWeight={600} sx={{ mt: 3 }}>
              Dirección del Negocio
            </Typography>
            <Divider sx={{ mb: 2 }} />

            <Typography variant="body2" color="primary" fontWeight={600} sx={{ mb: 1 }}>
              Dirección fiscal o dirección del negocio<span style={{ color: 'red' }}>*</span>
            </Typography>
            <FormControl fullWidth error={!!errores.ubicacion}>
              <Select
                value={service.ubicacion}
                onChange={(e) => {
                  if (e.target.value === '__nueva__') {
                    handleAddNewAddress();
                  } else {
                    handleChange("ubicacion", e.target.value);
                  }
                }}
                displayEmpty
                startAdornment={
                  <InputAdornment position="start">
                    <LocationOnIcon fontSize="small" sx={{ color: '#737791' }} />
                  </InputAdornment>
                }
                sx={{
                  '& .MuiSelect-select': {
                    display: 'flex',
                    alignItems: 'center',
                  }
                }}
              >
                <MenuItem value="" disabled>
                  <em style={{ color: '#737791', fontStyle: 'normal' }}>Selecciona tu dirección</em>
                </MenuItem>
                {direcciones?.map(dir => (
                  <MenuItem key={dir._id} value={dir._id}>{dir.nombre}</MenuItem>
                ))}
                <MenuItem value="__nueva__">
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <AddIcon fontSize="small" color="primary" />
                    <Typography variant="body2" color="primary">Agregar dirección</Typography>
                  </Box>
                </MenuItem>
              </Select>
              {errores.ubicacion && <FormHelperText error>{errores.ubicacion}</FormHelperText>}
            </FormControl>
          </Grid>

          <Grid item size={12}>
            <Typography variant="body1" color="primary" fontWeight={600} sx={{ mt: 3 }}>
              Categorización
            </Typography>
            <Divider sx={{ mb: 3 }} />
            <Grid container spacing={3}>
              <Grid item size={{ xs: 12, md: 6 }}>
                <Typography variant="body2" color="primary" fontWeight={600} sx={{ mb: 1 }}>
                  Categoría<span style={{ color: 'red' }}>*</span>
                </Typography>
                <FormControl fullWidth error={!!errores.categoria}>
                  <Select
                    value={service.categoria}
                    onChange={(e) => handleChange("categoria", e.target.value)}
                    displayEmpty
                  >
                    <MenuItem value="">
                      <em>Selecciona</em>
                    </MenuItem>
                    {categories?.map(cat => (
                      <MenuItem key={cat._id} value={cat._id}>{cat.name}</MenuItem>
                    ))}
                  </Select>
                  {errores.categoria && <FormHelperText error>{errores.categoria}</FormHelperText>}
                </FormControl>
              </Grid>
              <Grid item size={{ xs: 12, md: 6 }}>
                <Typography variant="body2" color="primary" fontWeight={600} sx={{ mb: 1 }}>
                  Subcategoría (Opcional)
                </Typography>
                <FormControl fullWidth>
                  <Select
                    value={service.subcategoria}
                    onChange={(e) => handleChange("subcategoria", e.target.value)}
                    displayEmpty
                  >
                    <MenuItem value="">
                      <em>Selecciona</em>
                    </MenuItem>
                    {subcategorias?.map(sub => (
                      <MenuItem key={sub._id} value={sub._id}>{sub.name}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </Grid>
        </Grid>

        {isGroup && (
          <Box sx={{ my: 5 }}>
            <Typography variant="subtitle1" fontWeight='bold' color='primary'>
              Miembros
            </Typography>
            <Typography
              variant="body2"
              color="#737791"
              sx={{ marginBottom: 2 }}
            >
              Busca a los miembros y agrégalos a tu grupo, recibirán de inmediato tu invitación
            </Typography>

            <Autocomplete
              fullWidth
              sx={{ mb: 2 }}
              options={memberInput.length >= 3 ? (searchResults || []) : []}
              getOptionLabel={(option) => `${option.name} ${option.apellido || ""} (${option.email})`}
              inputValue={memberInput}
              onInputChange={handleSearchChange}
              onChange={handleAddMember}
              value={selectedMember}
              renderInput={(params) => (
                <TextField
                  {...params}
                  size="small"
                  placeholder="Busca por nombre o email"
                  InputProps={{
                    ...params.InputProps,
                    startAdornment: (
                      <>
                        <InputAdornment position="start">
                          <Typography color="#737791">@</Typography>
                        </InputAdornment>
                        {params.InputProps.startAdornment}
                      </>
                    ),
                  }}
                />
              )}
              renderOption={(props, option) => (
                <li {...props}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Avatar
                      src={`${urlImg}/w_40,h_40,c_fill/v${option.versionAvatar}/users/${option._id}/${option.avatar}`}
                      sx={{ width: 24, height: 24 }}
                    />
                    <Box>
                      <Typography variant="body2">{option.name} {option.apellido}</Typography>
                      <Typography variant="caption">{option.email}</Typography>
                    </Box>
                  </Box>
                </li>
              )}
              noOptionsText={memberInput.length >= 3 ? "No se encontraron usuarios" : "Escribe al menos 3 letras"}
            />

            <Grid container spacing={3} alignItems="center">
              <Grid item size={{ xs: 12, md: 8 }}>
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                  {members && members.length > 0 ? (
                    members.map((member) => {
                      const user = member.usuario || member;
                      const isPageAdmin = (user._id || user).toString() === currentUser._id.toString();
                      const status = member.status !== undefined ? member.status : 1;

                      return (
                        <Box key={user._id} sx={{ position: 'relative', mb: 1 }}>
                          <Chip
                            avatar={
                              <Avatar
                                alt={user.name}
                                src={`${urlImg}/w_40,h_40,c_fill/v${user.versionAvatar}/users/${user._id}/${user.avatar}`}
                              />
                            }
                            label={`${user.name} ${user.apellido || ""}`}
                            onDelete={isPageAdmin ? undefined : () => handleMemberDelete(user._id)}
                            deleteIcon={isPageAdmin ? null : <CloseIcon />}
                            sx={{
                              bgcolor: '#f5f5f5',
                              borderRadius: '8px',
                              borderBottom: `3px solid ${getStatusColor(status)}`
                            }}
                          />
                          {!isPageAdmin && (
                            <Typography variant="caption" sx={{
                              position: 'absolute',
                              bottom: -12,
                              left: 4,
                              fontSize: '9px',
                              fontWeight: 'bold',
                              color: getStatusColor(status)
                            }}>
                              {getStatusText(status)}
                            </Typography>
                          )}
                        </Box>
                      );
                    })
                  ) : (
                    <Typography variant="body2">No hay miembros añadidos.</Typography>
                  )}
                </Box>
              </Grid>
              <Grid item size={{ xs: 12, md: 4 }} sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                <Button
                  type="button"
                  variant="contained"
                  onClick={handleCommitMember}
                  disabled={!selectedMember && members.length === 0}
                  fullWidth
                >
                  Añadir miembro
                </Button>
              </Grid>
            </Grid>
          </Box>
        )}

        <Box sx={{ my: 5 }}>
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
            <Box>
              <Typography variant="subtitle1" fontWeight='bold' color='primary'>
                Servicios de la Página
              </Typography>
              <Typography variant="body2" color="#737791">
                Agrega servicios a tu página desde tus servicios existentes o crea uno nuevo
              </Typography>
            </Box>
            <Button
              variant="contained"
              className="btn btn-secondary"
              onClick={handleCreateNewService}
            >
              Crear un nuevo servicio
            </Button>
          </Box>
          <Divider sx={{ mb: 3 }} />

          <Box sx={{ maxHeight: 600, overflowY: "auto", pr: 1 }}>
            {userServices && userServices.length > 0 ? (
              userServices.map(service => (
                <ServiceCard key={service._id} service={service} />
              ))
            ) : (
              <Paper sx={{ p: 4, textAlign: "center", bgcolor: "#f8f9fa", borderRadius: "12px" }}>
                <Typography variant="body1" color="textSecondary" gutterBottom>
                  No tienes servicios creados aún.
                </Typography>
                <Button
                  variant="text"
                  color="primary"
                  onClick={handleCreateNewService}
                  sx={{ textTransform: "none", fontWeight: "bold" }}
                >
                  Haz clic aquí para crear tu primer servicio
                </Button>
              </Paper>
            )}
          </Box>
        </Box>

        <Grid container spacing={2} mt={3} justifyContent="flex-end">
          <Button
            variant="outlined"
            onClick={e => handleSubmit(e, true)}
            type="button"
            disabled={loading}
          >
            Guardar como borrador
          </Button>
          <Button
            color="error"
            variant="contained"
            onClick={() => irA("/user/servicios", { state: { tab: 1 } })}
            disabled={loading}
          >
            Cancelar
          </Button>
          <Button
            variant="contained"
            color="primary"
            type="submit"
            disabled={loading}
            startIcon={loading ? <CircularProgress size={20} color="inherit" /> : null}
          >
            {loading ? "Procesando..." : "Publicar página"}
          </Button>
        </Grid>

        <Snackbar
          open={alert.open}
          autoHideDuration={4000}
          onClose={() => setAlert({ ...alert, open: false })}
          anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
          sx={{ top: '30% !important' }} 
        >
          <Alert 
            onClose={() => setAlert({ ...alert, open: false })} 
            severity={alert.severity} 
            sx={{ 
              width: '100%', 
              minWidth: { xs: '300px', md: '500px' },
              fontSize: '1.2rem',
              py: 2,
              px: 4,
              borderRadius: '12px',
              boxShadow: '0 8px 32px rgba(0,0,0,0.2)',
              '& .MuiAlert-icon': { fontSize: '2rem' }
            }}
          >
            {alert.message}
          </Alert>
        </Snackbar>

      </form >
    </Box >
  )
}

FormPagina.propTypes = {
  irA: PropTypes.func.isRequired,
  type: PropTypes.string
}

export default FormPagina;
